/**
 * CurrencyInput/NumberInput emit `number | undefined` on every keystroke (undefined when blank),
 * so the fields below are already real numbers or absent by the time a save reaches this point —
 * this is a backstop, not a live conversion path, against a future numeric field being wired up
 * to a raw text input (or otherwise assigned a stray string) without going through
 * CurrencyInput/NumberInput. FormInput is text-only (email/tel/date/month) and never emits a
 * numeric-field value at all, so it can't be the source of a stray string here either.
 * `assets[].costBase` used to be excluded here (it doubled as a 'Pre CGT' text marker), but the
 * pre-1985 exemption is now derived from the asset's recorded acquisition month (see
 * capitalGainsTax.ts's isPreCgtAsset), so costBase normalizes like every other numeric field below.
 * `acquiredOn` is a `YYYY-MM` string from a FormInput and is deliberately NOT listed as numeric.
 */
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import type { FactFindAsset, FactFindLiability, FactFindAssetMap } from '../../../shared/src/calculation/factFindTypes.ts';

export const NUMERIC_FIELDS: Record<string, readonly string[]> = {
  home: ['value', 'homeGrowthRate', 'likelyRent', 'likelyExpenses', 'landSizeValue', 'separateTitlesValue', 'businessUseValue', 'equityReleaseAmount', 'commercialReverseMortgageAmount'],
  rent: ['rentAmount'],
  grannyFlat: ['amountPaid', 'propertyTransferredValue', 'constructionCost'],
  retirementVillage: ['entryContribution', 'ongoingFees', 'yearsResidency', 'dmfPctPerYear', 'dmfCapYears', 'capitalGainSharePct', 'expectedResaleValue', 'reinstatementCost', 'refundMonths', 'expectedResaleMonths'],
  livingWithFamily: ['saleProceeds', 'boardPerWeek'],
  careStay: ['roomPrice', 'radPaid', 'dailyPayment', 'contributionsPaidThisYear', 'contributionsPaidToDate'],
  assetItem: ['value', 'incomeRate', 'growthRate', 'frankingCredit', 'costBase', 'rent', 'expenses', 'purchasePrice', 'rcv', 'income', 'guaranteePeriodYears', 'reversionPct'],
  liabilityItem: ['balance', 'repayments', 'interestRate', 'loanTerm'],
  structureItem: ['apportionmentClient', 'apportionmentPartner', 'annualDistributionPa'],
  otherIncomeItem: ['amount', 'dbPaymentAmount', 'dbDeductibleAmount', 'dbIndexationPct', 'dbReversionPct'],
  expenses: ['livingExpenses'],
  capitalExpenseItem: ['amount'],
  /** frontend/App.js's coupleValidation useState — CurrencyInput/raw-number-input backed, so
   *  already numbers in practice, but sellingCosts is explicitly kept as '' when cleared
   *  (see CoupleValidationModal.js) and needs the same drop-when-blank treatment as everything else. */
  coupleValidationItem: ['partnerAnnualRent', 'sellingCosts', 'emergencyFund', 'partnerEntryYear', 'partnerFutureRoomPrice', 'stampDuty'],
};

/**
 * Numeric string -> number; blank/non-numeric -> key deleted (the schema treats these as optional).
 * `obj` is intentionally `unknown` — this is called against many differently-shaped fact-find
 * sub-objects (home, rent, an asset row, a couple-validation bag, ...); narrowed to a plain
 * string-keyed record before use.
 */
export function normalizeNumericFieldsOn(obj: unknown, fields: readonly string[]): void {
  if (!obj || typeof obj !== 'object') return;
  const rec = obj as Record<string, unknown>;
  for (const field of fields) {
    if (!(field in rec)) continue;
    const value = rec[field];
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed === '') {
      delete rec[field];
      continue;
    }
    const parsed = parseFloat(trimmed);
    if (Number.isNaN(parsed)) delete rec[field];
    else rec[field] = parsed;
  }
}

/** Mutates `fd` in place, converting every numeric-ish field the fact-find schema types as a
 *  plain number (see NUMERIC_FIELDS). Callers should pass an already-cloned object. */
export function normalizeNumericFactFindFields(fd: DraftFactFind | null | undefined): void {
  if (!fd) return;
  normalizeNumericFieldsOn(fd.home, NUMERIC_FIELDS.home!);
  normalizeNumericFieldsOn(fd.rent, NUMERIC_FIELDS.rent!);
  normalizeNumericFieldsOn(fd.grannyFlat, NUMERIC_FIELDS.grannyFlat!);
  normalizeNumericFieldsOn(fd.retirementVillage, NUMERIC_FIELDS.retirementVillage!);
  normalizeNumericFieldsOn(fd.livingWithFamily, NUMERIC_FIELDS.livingWithFamily!);
  normalizeNumericFieldsOn(fd.client?.careStay, NUMERIC_FIELDS.careStay!);
  normalizeNumericFieldsOn(fd.partner?.careStay, NUMERIC_FIELDS.careStay!);
  normalizeAssetsMap(fd.assets);
  for (const list of Object.values(fd.liabilities || {})) {
    if (Array.isArray(list)) list.forEach((item) => normalizeNumericFieldsOn(item, NUMERIC_FIELDS.liabilityItem!));
  }
  if (Array.isArray(fd.structures)) fd.structures.forEach((item) => normalizeNumericFieldsOn(item, NUMERIC_FIELDS.structureItem!));
  if (Array.isArray(fd.income?.otherIncomes)) fd.income.otherIncomes.forEach((item) => normalizeNumericFieldsOn(item, NUMERIC_FIELDS.otherIncomeItem!));
  normalizeNumericFieldsOn(fd.expenses, NUMERIC_FIELDS.expenses!);
  if (Array.isArray(fd.expenses?.capitalExpenses)) fd.expenses.capitalExpenses.forEach((item) => normalizeNumericFieldsOn(item, NUMERIC_FIELDS.capitalExpenseItem!));
}

/** A single fact-find asset row (e.g. a bare `asset`/`propertyAsset` argument, not nested in factFind). */
export function normalizeAssetItem(item: FactFindAsset | Record<string, unknown> | null | undefined): void {
  normalizeNumericFieldsOn(item, NUMERIC_FIELDS.assetItem!);
}

/** A single fact-find liability row. */
export function normalizeLiabilityItem(item: FactFindLiability | Record<string, unknown> | null | undefined): void {
  normalizeNumericFieldsOn(item, NUMERIC_FIELDS.liabilityItem!);
}

/** The `{ [categoryId]: AssetItem[] }` map (factFind.assets, or a bare `assets` argument). */
export function normalizeAssetsMap(assets: FactFindAssetMap | Record<string, unknown> | null | undefined): void {
  for (const list of Object.values(assets || {})) {
    if (Array.isArray(list)) list.forEach((item) => normalizeNumericFieldsOn(item, NUMERIC_FIELDS.assetItem!));
  }
}
